import React, { useState, useRef } from 'react';
import {
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  IconButton
} from '@material-ui/core';
import {
  CloudUpload,
  Description,
  Delete
} from '@material-ui/icons';
import TopMenu from '../TopMenu';
import NewCandidateAnalysisService from '../../services/NewCandidateAnalysisService';
import PdfTextExtractionService from '../../services/PdfTextExtractionService';
import swal from 'sweetalert';
import './NewCandidateAnalysis.css';

const NewCandidateAnalysis = () => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [jdFileName, setJdFileName] = useState('');
  const [jdText, setJdText] = useState('');
  const [examCodesInput, setExamCodesInput] = useState('');
  const [evaluationParams, setEvaluationParams] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [stageProgress, setStageProgress] = useState({});
  const [extractingJd, setExtractingJd] = useState(false);
  const fileInputRef = useRef(null);

  const analysisStages = [
    'Validating Input Parameters',
    'Extracting Job Description',
    'Extracting Competencies from JD',
    'Mapping Questions to JD Competencies',
    'Fetching Assessment Data', 
    'Calculating Competency Scores',
    'Generating JD-Based Analysis',
    'Compiling Final Report'
  ];

  // Parse exam codes from input
  const parseExamCodes = (inputText) => {
    if (!inputText.trim()) return [];
    const codes = inputText.split(',').map(code => code.trim()).filter(code => code);
    return codes;
  };

  // Handle JD file selection
  const handleJdFileSelect = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF file for Job Description');
      return;
    }
    
    setJdFile(file);
    setJdFileName(file.name);
    setError('');
    setExtractingJd(true);
    
    try {
      // Extract text from PDF
      const extractionResult = await PdfTextExtractionService.extractText(file);
      
      if (extractionResult.error) {
        throw new Error(extractionResult.error);
      }
      
      setJdText(extractionResult.text || '');
      
      // Try to extract role from JD text
      if (extractionResult.text) {
        const roleMatch = extractRoleFromJd(extractionResult.text);
        if (roleMatch) {
          setRole(roleMatch);
        }
      }
      
    } catch (error) {
      console.error('JD extraction error:', error);
      setError(`Failed to extract text from PDF: ${error.message}`);
      setJdFile(null);
      setJdFileName('');
      setJdText('');
    } finally {
      setExtractingJd(false);
    }
  };

  // Extract role from JD text using simple pattern matching
  const extractRoleFromJd = (text) => {
    const textLower = text.toLowerCase();
    
    // Common role patterns
    const rolePatterns = [
      /(?:role|position|title|job title)[\s:]+([A-Z][A-Za-z\s&]+?)(?:\n|\.|,|$)/i,
      /(?:looking for|seeking|hiring)[\s:]+([A-Z][A-Za-z\s&]+?)(?:\n|\.|,|$)/i,
      /(?:software engineer|developer|programmer|engineer|analyst|manager|lead|architect|specialist|consultant)/i
    ];
    
    for (const pattern of rolePatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const role = match[1].trim();
        return role.replace(/^(role|position|title|job title)[\s:]+/i, '').trim();
      }
    }
    
    // Fallback: look for common role keywords
    if (textLower.includes('software engineer') || textLower.includes('sde')) return 'Software Engineer';
    if (textLower.includes('full stack')) return 'Full Stack Developer';
    if (textLower.includes('backend')) return 'Backend Developer';
    if (textLower.includes('frontend') || textLower.includes('front-end')) return 'Frontend Developer';
    if (textLower.includes('data scientist')) return 'Data Scientist';
    if (textLower.includes('devops')) return 'DevOps Engineer';
    if (textLower.includes('qa') || textLower.includes('quality assurance')) return 'QA Engineer';
    if (textLower.includes('test engineer')) return 'Test Engineer';
    if (textLower.includes('tse') || textLower.includes('technical support')) return 'TSE';
    
    return null;
  };

  // Remove JD file
  const removeJdFile = () => {
    setJdFile(null);
    setJdFileName('');
    setJdText('');
    setRole('');
  };

  const updateStageProgress = (stage, status, message = '') => {
    setStageProgress(prev => ({
      ...prev,
      [stage]: { status, message, timestamp: new Date().toISOString() }
    }));
    
    if (status === 'completed') {
      setCurrentStage(stage + 1);
    }
  };

  const resetProgress = () => {
    setCurrentStage(0);
    setStageProgress({});
  };

  const handleAnalysis = async () => {
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    if (!jdFile || !jdText) {
      setError('Job Description PDF is mandatory for analysis');
      return;
    }

    if (!role.trim()) {
      setError('Please enter or extract a role from the Job Description');
      return;
    }

    // Parse exam codes from input
    const parsedCodes = parseExamCodes(examCodesInput);
    
    if (parsedCodes.length < 2) {
      setError('Please enter at least 2 exam codes');
      return;
    }

    if (parsedCodes.length > 5) {
      setError('Maximum 5 exam codes are allowed per analysis');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysis(null);
    setShowProgressModal(true);
    resetProgress();

    try {
      // Stage 1: Validating Input Parameters
      updateStageProgress(0, 'in-progress', 'Checking email, JD, and exam codes...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Process email: replace dots with commas ONLY for Firebase search
      const processedEmail = email.trim().replace(/\./g, ',');
      
      updateStageProgress(0, 'completed', `Validated ${parsedCodes.length} exam codes (${parsedCodes.length >= 2 && parsedCodes.length <= 5 ? 'valid' : 'invalid'})`);

      // Stage 2: Extracting Job Description (if not already extracted)
      if (!jdText) {
        updateStageProgress(1, 'in-progress', 'Extracting text from Job Description PDF...');
        const extractionResult = await PdfTextExtractionService.extractText(jdFile);
        if (extractionResult.error) {
          throw new Error(`Failed to extract JD: ${extractionResult.error}`);
        }
        setJdText(extractionResult.text || '');
        updateStageProgress(1, 'completed', 'Job Description extracted successfully');
      } else {
        updateStageProgress(1, 'completed', 'Job Description already extracted');
      }

      // Stage 3: Extracting Competencies from JD
      updateStageProgress(2, 'in-progress', 'Extracting required competencies from Job Description...');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStageProgress(2, 'completed', 'JD competencies extracted');

      // Stage 4: Mapping Questions to JD Competencies
      updateStageProgress(3, 'in-progress', 'Mapping exam questions to JD competencies...');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStageProgress(3, 'completed', 'Questions mapped to JD competencies');

      // Stage 5: Fetching Assessment Data
      updateStageProgress(4, 'in-progress', 'Retrieving assessment data from database...');
      
      // Stage 6: Calculating Competency Scores
      updateStageProgress(5, 'in-progress', 'Computing scores for JD competencies...');
      
      // Stage 7: Generating JD-Based Analysis
      updateStageProgress(6, 'in-progress', 'Generating suitability analysis based on JD requirements...');

      // Use the processed email for Firebase search, but keep original for display
      const result = await NewCandidateAnalysisService.analyzeCandidateWithJD(
        processedEmail, 
        role, 
        parsedCodes,
        jdText,
        evaluationParams
      );
      
      // Mark stages as completed
      updateStageProgress(4, 'completed', 'Assessment data retrieved successfully');
      updateStageProgress(5, 'completed', 'JD competency scores calculated');
      updateStageProgress(6, 'completed', 'JD-based analysis generated');
      
      // Stage 8: Compiling Final Report
      updateStageProgress(7, 'in-progress', 'Compiling final suitability report...');
      await new Promise(resolve => setTimeout(resolve, 800));
      updateStageProgress(7, 'completed', 'Suitability report ready');
      
      setAnalysis(result);
      
      // Update the success message
      setTimeout(() => {
        setShowProgressModal(false);
        swal({
          title: 'Profile Generation Complete',
          text: 'Candidate profile has been generated successfully',
          icon: 'success',
          timer: 2000
        });
      }, 1000);
      
    } catch (error) {
      console.error('Analysis error:', error);
      setError(error.message || 'Failed to analyze candidate. Please try again.');
      setShowProgressModal(false);
      
      // Update the error message
      swal({
        title: 'Profile Generation Failed',
        text: error.message || 'Failed to generate candidate profile',
        icon: 'error',
        timer: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Results display - JD-based format
  const renderAnalysisResults = (analysis) => {
    if (!analysis) return null;

    const getPercentageClass = (percentage) => {
      if (percentage >= 90) return 'new-percentage-excellent';
      if (percentage >= 80) return 'new-percentage-good';
      if (percentage >= 70) return 'new-percentage-average';
      if (percentage >= 50) return 'new-percentage-poor';
      return 'new-percentage-very-poor';
    };

    const getLevelClass = (level) => `new-level-${level}`;

    const getSuitabilityClass = (suitability) => {
      if (suitability === 'SUITABLE') return { bg: '#e8f5e9', color: '#2e7d32', text: '✓ SUITABLE' };
      if (suitability === 'PARTIALLY_SUITABLE') return { bg: '#fff3e0', color: '#f57c00', text: '⚠ PARTIALLY SUITABLE' };
      return { bg: '#ffebee', color: '#c62828', text: '✗ NOT SUITABLE' };
    };

    const suitabilityStyle = analysis.suitability ? getSuitabilityClass(analysis.suitability) : null;

    return (
      <div className="new-analysis-results">
        {/* 1. Candidate Details & Suitability */}
        <div className="new-section">
          <h2>1. Candidate Details & Suitability Assessment</h2>
          <table className="new-basic-table">
            <tbody>
              <tr>
                <td><strong>Name</strong></td>
                <td>{analysis.candidateName}</td>
              </tr>
              <tr>
                <td><strong>Email</strong></td>
                <td>{analysis.candidateEmail}</td>
              </tr>
              <tr>
                <td><strong>Role</strong></td>
                <td>{analysis.role}</td>
              </tr>
              <tr>
                <td><strong>Exam Codes</strong></td>
                <td>{analysis.examCodes?.join(', ')}</td>
              </tr>
              <tr>
                <td><strong>Total Assessments</strong></td>
                <td>{analysis.totalAssessments}</td>
              </tr>
              {analysis.suitability && (
                <tr>
                  <td><strong>Suitability</strong></td>
                  <td>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '4px',
                      backgroundColor: suitabilityStyle.bg,
                      color: suitabilityStyle.color,
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}>
                      {suitabilityStyle.text}
                    </span>
                  </td>
                </tr>
              )}
              {analysis.suitabilityReason && (
                <tr>
                  <td><strong>Suitability Assessment</strong></td>
                  <td className="new-interpretation-text">{analysis.suitabilityReason}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 2. JD-Based Competency Performance */}
        <div className="new-section">
          <h2>2. Performance by JD Competencies</h2>
          <table className="new-basic-table">
            <thead>
              <tr>
                <th>JD Competency</th>
                <th>Importance</th>
                <th>Score</th>
                <th>Percentage</th>
                <th>Level</th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              {analysis.competencyScores?.map((score, index) => {
                const compAnalysis = analysis.competencyAnalysis?.find(c => c.competency === score.competency);
                return (
                  <tr key={index}>
                    <td className="new-competency-name">
                      <div><strong>{score.competency}</strong></div>
                      {score.description && (
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                          {score.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: score.importance === 'high' ? '#ffebee' : 
                                        score.importance === 'medium' ? '#fff3e0' : '#e3f2fd',
                        color: score.importance === 'high' ? '#c62828' : 
                               score.importance === 'medium' ? '#f57c00' : '#1976d2',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {score.importance || 'medium'}
                      </span>
                    </td>
                    <td><span className="new-score-text">{score.raw}</span></td>
                    <td><span className={getPercentageClass(score.percentage)}>{score.percentage}%</span></td>
                    <td><span className={getLevelClass(score.readinessLevel)}>Level {score.readinessLevel}</span></td>
                    <td>
                      {compAnalysis && (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: compAnalysis.performance === 'EXCELLENT' ? '#e8f5e9' :
                                          compAnalysis.performance === 'GOOD' ? '#e3f2fd' :
                                          compAnalysis.performance === 'AVERAGE' ? '#fff3e0' : '#ffebee',
                          color: compAnalysis.performance === 'EXCELLENT' ? '#2e7d32' :
                                 compAnalysis.performance === 'GOOD' ? '#1976d2' :
                                 compAnalysis.performance === 'AVERAGE' ? '#f57c00' : '#c62828',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          {compAnalysis.performance}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 3. Detailed Competency Analysis */}
        {analysis.competencyAnalysis && analysis.competencyAnalysis.length > 0 && (
          <div className="new-section">
            <h2>3. Detailed Competency Analysis vs JD Requirements</h2>
            {analysis.competencyAnalysis.map((comp, index) => (
              <div key={index} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ color: '#2c3e50', marginBottom: '10px' }}>{comp.competency}</h3>
                <table className="new-basic-table" style={{ marginBottom: '10px' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '200px' }}><strong>Alignment with JD:</strong></td>
                      <td className="new-interpretation-text">{comp.alignmentWithJD}</td>
                    </tr>
                    <tr>
                      <td><strong>Strengths:</strong></td>
                      <td className="new-interpretation-text" style={{ color: '#2e7d32' }}>{comp.strengths || 'None identified'}</td>
                    </tr>
                    <tr>
                      <td><strong>Areas for Improvement:</strong></td>
                      <td className="new-interpretation-text" style={{ color: '#c62828' }}>{comp.improvementAreas || 'None identified'}</td>
                    </tr>
                    <tr>
                      <td><strong>Recommendations:</strong></td>
                      <td className="new-interpretation-text">{comp.recommendations || 'Continue current development'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* 4. Overall Strengths */}
        {analysis.overallStrengths && analysis.overallStrengths.length > 0 && (
          <div className="new-section">
            <h2>4. Key Strengths (Matching JD Requirements)</h2>
            <ul style={{ paddingLeft: '20px' }}>
              {analysis.overallStrengths.map((strength, index) => (
                <li key={index} className="new-interpretation-text" style={{ marginBottom: '8px' }}>
                  <strong style={{ color: '#2e7d32' }}>✓</strong> {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 5. Critical Gaps */}
        {analysis.criticalGaps && analysis.criticalGaps.length > 0 && (
          <div className="new-section">
            <h2>5. Critical Gaps (vs JD Requirements)</h2>
            <ul style={{ paddingLeft: '20px' }}>
              {analysis.criticalGaps.map((gap, index) => (
                <li key={index} className="new-interpretation-text" style={{ marginBottom: '8px' }}>
                  <strong style={{ color: '#c62828' }}>⚠</strong> {gap}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 6. Improvement Plan */}
        {analysis.improvementPlan && (
          <div className="new-section">
            <h2>6. Improvement Plan</h2>
            <table className="new-basic-table">
              <thead>
                <tr>
                  <th>Immediate Actions</th>
                  <th>Long-term Development</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {analysis.improvementPlan.immediateActions?.map((action, index) => (
                        <li key={index} className="new-interpretation-text">{action}</li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {analysis.improvementPlan.longTermDevelopment?.map((dev, index) => (
                        <li key={index} className="new-interpretation-text">{dev}</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* 7. Suggested Interview Questions */}
        {analysis.suggestedInterviewQuestions && analysis.suggestedInterviewQuestions.length > 0 && (
          <div className="new-section">
            <h2>7. Suggested Interview Questions</h2>
            <table className="new-basic-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Question</th>
                </tr>
              </thead>
              <tbody>
                {analysis.suggestedInterviewQuestions.map((question, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: 'center', fontWeight: '600', color: '#2c3e50' }}>{index + 1}</td>
                    <td className="new-interpretation-text">{question}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 8. Overall Summary */}
        <div className="new-section">
          <h2>8. Overall Assessment & Hiring Recommendation</h2>
          <div className="new-summary-content">
            <p>{analysis.overallSummary || 'Overall assessment summary will be generated here.'}</p>
          </div>
        </div>
      </div>
    );
  };

  // Form rendering
  const renderAnalysisForm = () => {
    const parsedCodes = parseExamCodes(examCodesInput);
    const codesCount = parsedCodes.length;
    const isValidCodesCount = codesCount >= 2 && codesCount <= 5;
    
    return (
      <div className="new-analysis-form">
        <h2>Candidate Profiling</h2>
        
        {/* Row 1: Email, JD Upload, Role - All in one row */}
        <div className="new-form-row">
          <div className="new-form-group" style={{ flex: 1 }}>
            <label htmlFor="email">Candidate Email:</label>
            <TextField
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter candidate email"
              fullWidth
              variant="outlined"
              required
            />
          </div>
          <div className="new-form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="jdFile" style={{ marginBottom: '8px' }}>Job Description (PDF only) *Required:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleJdFileSelect(file);
                }
              }}
              style={{ display: 'none' }}
              id="jdFile"
            />
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              minHeight: '56px',
              padding: '0 14px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px',
              backgroundColor: 'transparent'
            }}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || extractingJd}
                size="small"
                style={{ minWidth: '120px' }}
              >
                Choose File
              </Button>
              {jdFileName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }}>
                  <Description style={{ color: '#1976d2', fontSize: '18px' }} />
                  <Typography variant="body2" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {jdFileName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={removeJdFile}
                    disabled={loading || extractingJd}
                    style={{ padding: '4px' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </div>
              )}
              {extractingJd && (
                <CircularProgress size={18} />
              )}
            </div>
            {jdText && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px', display: 'block', fontSize: '0.75rem' }}>
                ✓ JD extracted ({jdText.length} chars)
              </Typography>
            )}
            {!jdText && (
              <Typography variant="caption" color="error" style={{ marginTop: '4px', display: 'block', fontSize: '0.75rem' }}>
                ⚠ JD mandatory
              </Typography>
            )}
          </div>
          <div className="new-form-group" style={{ flex: 1 }}>
            <label htmlFor="role">Role (extracted from JD or enter manually):</label>
            <TextField
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Role will be extracted from JD or enter manually"
              fullWidth
              variant="outlined"
              required
              helperText={role ? "✓ Role entered" : "Enter role or wait for extraction"}
            />
          </div>
        </div>

        {/* Row 2: Exam Codes - Full width */}
        <div className="new-form-row">
          <div className="new-form-group" style={{ flex: 1 }}>
            <label htmlFor="examCodes">Exam Codes (Comma Separated, Min: 2, Max: 5):</label>
            <TextField
              id="examCodes"
              value={examCodesInput}
              onChange={(e) => {
                setExamCodesInput(e.target.value);
              }}
              placeholder="Enter 2-5 exam codes separated by commas (e.g., GSCSA001, GSCSA002, GSCSA003)"
              fullWidth
              variant="outlined"
              required
              helperText={(() => {
                if (codesCount === 0) {
                  return "Enter 2-5 exam codes separated by commas";
                } else if (codesCount < 2) {
                  return `Enter ${2 - codesCount} more exam code(s). Minimum 2 required.`;
                } else if (codesCount >= 2 && codesCount <= 5) {
                  return `✅ ${codesCount} exam code(s) entered correctly (${codesCount}/5)`;
                } else {
                  return `❌ Too many codes (${codesCount}/5). Maximum 5 codes allowed.`;
                }
              })()}
              error={codesCount > 5}
            />
          </div>
        </div>

        {/* Row 3: Evaluation Parameters - Full width */}
        <div className="new-form-row">
          <div className="new-form-group" style={{ flex: 1 }}>
            <label htmlFor="evaluationParams">Evaluation Parameters (Optional):</label>
            <TextField
              id="evaluationParams"
              value={evaluationParams}
              onChange={(e) => setEvaluationParams(e.target.value)}
              placeholder="Enter any specific evaluation parameters or criteria (e.g., focus on problem-solving, emphasize communication skills, etc.)"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              helperText="Optional: Specify any additional evaluation criteria or focus areas for the analysis"
            />
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="new-form-row">
          <Button
            className="new-analyze-button"
            variant="contained"
            color="primary"
            onClick={handleAnalysis}
            disabled={loading || !email || !role || !jdFile || !jdText || !isValidCodesCount || extractingJd}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : 'Generate Candidate Profile'}
          </Button>
        </div>
      </div>
    );
  };

  // Progress modal
  const renderProgressModal = () => {
    return (
      <Dialog open={showProgressModal} maxWidth="sm" fullWidth>
        <DialogTitle>Generating Candidate Profile</DialogTitle>
        <DialogContent className="new-progress-modal">
          <LinearProgress />
          <div className="new-progress-stages">
            {analysisStages.map((stage, index) => {
              const stageData = stageProgress[index];
              const isActive = index === currentStage;
              const isCompleted = stageData?.status === 'completed';
              const isInProgress = stageData?.status === 'in-progress';

              return (
                <div key={stage} className={`new-stage ${stageData?.status || 'pending'}`}>
                  {stage}
                  {isCompleted && <span style={{ color: '#4caf50', marginLeft: '8px' }}>✓</span>}
                  {isInProgress && <CircularProgress size={16} style={{ color: '#1976d2', marginLeft: '8px' }} />}
                </div>
              );
            })}
          </div>
          
          {currentStage >= analysisStages.length && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <div style={{ color: '#4caf50', fontSize: '48px', marginBottom: '8px' }}>✓</div>
              <Typography variant="h6" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                Profile Generation Complete!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Preparing your comprehensive candidate profile...
              </Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="new-candidate-analysis-container">
      <TopMenu showHome showLogout />
      <div className="new-candidate-analysis-header">
        <Typography variant="h4" style={{ fontSize: '24px' }}>
          Candidate Profiling Dashboard (New)
        </Typography>
      </div>
      
      {renderAnalysisForm()}
      
      {error && (
        <div className="new-error-message">
          {error}
        </div>
      )}
      
      {renderProgressModal()}
      
      {analysis && renderAnalysisResults(analysis)}
    </div>
  );
};

export default NewCandidateAnalysis;

